import DateInput from '@/common/components/base/input/dateInput/DateInput';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { PERSIAN_LATTER_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import Typography from '@/common/components/base/typography/Typography';
import FlowButton from '@/common/components/flowButton/FlowButton';
import usePersonalInquiryStore from '@/common/store/personalInquiry/usePersonalInquiry';
import { OwnerConstType } from '@/common/types/Files.type';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { NATIONAL_CODE_RULE } from '@/common/utils/rules/nationalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import PersonalInquiry from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/PersonalInquiry';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { getBodyOwnerUserInformationQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyOwnerUserInformation/getBodyOwnerUserInformationQueryOptions';
import { patchBodyOwnerUserInformationQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyOwnerUserInformation/patchBodyOwnerUserInformationQueryOptions';
import { BodyPersonalInformationOwnerType } from '@/modules/adjusterReportModules/Insurer-information/types/BodyOwnerType';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

export const BodyOwnerUserInformation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data } = useQuery(getBodyOwnerUserInformationQueryOptions(+id!));
  const { data: inquiryData, clearInquiryData } = usePersonalInquiryStore();
  const mutate = useMutation(patchBodyOwnerUserInformationQueryOptions());
  const methods = useForm<BodyPersonalInformationOwnerType>({
    mode: 'onChange',
  });
  const ownerTypeWatcher = useWatch({
    control: methods?.control,
    name: 'owner_type',
  });
  const submitHandler = () => {
    const temporaryObject: BodyPersonalInformationOwnerType = {};
    if (methods.getValues('owner_type') === 'legal') {
      temporaryObject.owner_type = methods.getValues(
        'owner_type',
      ) as OwnerConstType;
      temporaryObject.name = methods.getValues('name');
      temporaryObject.registration_number = methods.getValues(
        'registration_number',
      );
      temporaryObject.national_id = methods.getValues('national_id');
      temporaryObject.economic_code = methods.getValues('economic_code');
    } else {
      temporaryObject.owner_type = methods.getValues(
        'owner_type',
      ) as OwnerConstType;
      temporaryObject.first_name = methods.getValues('first_name');
      temporaryObject.last_name = methods.getValues('last_name');
      temporaryObject.father_name = !!methods.getValues('father_name')
        ? methods.getValues('father_name')
        : null;
      temporaryObject.national_code = methods.getValues('national_code');
      temporaryObject.identity_number = !!methods.getValues('identity_number')
        ? methods.getValues('identity_number')
        : null;
      temporaryObject.identity_issue_location = !!methods.getValues(
        'identity_issue_location',
      )
        ? methods.getValues('identity_issue_location')
        : null;
      temporaryObject.birthdate = convertPersianToGregorian(
        methods.getValues('birthdate') as string,
      );
      temporaryObject.gender = methods.getValues('gender');
      temporaryObject.owner_type = methods.getValues(
        'owner_type',
      ) as OwnerConstType;
    }
    mutate.mutate(
      { id: id!, formData: temporaryObject },
      {
        onSuccess: () => {
          clearInquiryData();
          navigate(
            ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
              id: id!,
            }).DRIVER_OWNER_INFORMATION.ownerContact,
            { replace: true },
          );
        },
      },
    );
  };

  useEffect(() => {
    if (data) {
      methods.reset({
        ...data.personal_information,
      });
    }
  }, [data, methods]);

  const formatBirthDate = (dateStr?: string) => {
    if (!dateStr) return undefined;
    let year = dateStr.substring(0, 4);
    let month = dateStr.substring(4, 6);
    let day = dateStr.substring(6, 8);
    return `${year}/${month}/${day}`;
  };

  useEffect(() => {
    if (inquiryData?.national_code) {
      methods.reset({
        ...methods.getValues(),
        birthdate: inquiryData?.birthdate
          ? formatBirthDate(inquiryData?.birthdate)
          : undefined,
        gender: inquiryData?.gender === 'MALE' ? 'male' : 'female',
        first_name: inquiryData?.first_name,
        last_name: inquiryData?.last_name,
        identity_number: String(inquiryData?.identification_number),
        identity_issue_location: inquiryData?.birth_place,
      });
    }
  }, [inquiryData]);

  const previousStep = () => {
    navigate(
      ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
        id: id!,
      }).DRIVER_OWNER_INFORMATION.driverContact,
      {
        replace: true,
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <Box py={24}>
        <PersonalInquiry outSideForm={useFormContext} />
      </Box>
      <form onSubmit={methods.handleSubmit(submitHandler)}>
        <Flex flexDirection="column" gap="10px">
          <React.Fragment>
            <RadioGroup
              className={css({
                flexDirection: 'row',
                gap: '2rem',
              })}
              title="نوع مالکیت"
              rules={{ required: 'نوع مالکیت را وارد کنید' }}
              name="owner_type"
              options={[
                { label: 'حقیقی', value: 'real' },
                { label: 'حقوقی', value: 'legal' },
              ]}
              renderRadioButton={(radioButton, value) => (
                <Box key={value} display="flex" alignItems="center" gap="10px">
                  {radioButton}
                </Box>
              )}
            />
            <Typography textStyle="b2" color={'red-3'}>
              {methods.formState.errors['owner_type']?.message}
            </Typography>
          </React.Fragment>
          {methods.getValues('owner_type') === 'real' ||
          ownerTypeWatcher === 'real' ? (
            <>
              <React.Fragment>
                <RadioGroup
                  className={css({
                    flexDirection: 'row',
                    gap: '2rem',
                  })}
                  title="جنسیت مالک"
                  name="gender"
                  options={[
                    { label: 'اقا', value: 'male' },
                    { label: 'خانم', value: 'female' },
                  ]}
                  rules={{ required: 'لطفا جنسیت مالک را انتخاب کنید' }}
                  renderRadioButton={(radioButton, value) => (
                    <Box
                      key={value}
                      display="flex"
                      alignItems="center"
                      gap="10px"
                    >
                      {radioButton}
                    </Box>
                  )}
                />
                <Typography textStyle="b2" color={'red'}>
                  {methods.formState.errors['gender']?.message}
                </Typography>
              </React.Fragment>
              <TextInput
                rules={{
                  required: 'وارد کردن نام اجباری است',
                  ...PERSIAN_LATTER_RULE,
                }}
                name="first_name"
                label="نام"
                key={'first_name'}
              />
              <TextInput
                rules={{
                  required: 'وارد کردن نام خانوادگی است',
                  ...PERSIAN_LATTER_RULE,
                }}
                name="last_name"
                label="نام خانوادگی"
                key={'last_name'}
              />
              <DateInput
                buttonText="ثبت"
                modalLabel="تاریخ تولد"
                rules={{
                  required: requiredText,
                  validate: validateNotFutureDate,
                }}
                name="birthdate"
                label="تاریخ تولد"
                key={'birthdate'}
              />
              <TextInput
                rules={{
                  required: { message: requiredText, value: true },
                  ...NATIONAL_CODE_RULE,
                }}
                max={10}
                name="national_code"
                label="کد ملی"
                noCharInput
                key={'national_code'}
              />
              <TextInput
                name="father_name"
                label="نام پدر"
                optionalLabel
                rules={{ ...PERSIAN_LATTER_RULE }}
              />
              <TextInput
                name="identity_number"
                label="شماره شناسنامه"
                rules={{
                  pattern: {
                    value: /^[0-9]*$/,
                    message: 'شماره شناسنامه نامعتبر است',
                  },
                }}
                optionalLabel
                noCharInput
                key={'identity_number'}
                max={10}
              />
              <TextInput
                name="identity_issue_location"
                label="محل صدور شناسنامه"
                optionalLabel
                key={'identity_issue_location'}
              />
            </>
          ) : (
            <>
              <TextInput
                rules={{ required: 'وارد کردن نام اجباری است' }}
                name="name"
                label="نام"
                key={'name'}
              />
              <TextInput
                rules={{ required: 'وارد کردن شماره ثبت اجباری است' }}
                name="registration_number"
                label="شماره ثبت"
                key={'registration_number'}
              />
              <TextInput
                name="economic_code"
                label="کد اقتصادی"
                rules={{ required: 'وارد کردن کد اقتصادی اجباری است' }}
                key={'economic_code'}
              />
              <TextInput
                name="national_id"
                label="شناسه ملی"
                rules={{ required: 'وارد کردن شناسه ملی اجباری است' }}
              />
            </>
          )}
          <FlowButton
            disabled={!methods?.formState?.isValid}
            previousStepHandler={previousStep}
          />
        </Flex>
      </form>
    </FormProvider>
  );
};
